import React, { useState } from 'react'
import { Node } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer, NodeViewProps } from '@tiptap/react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import ViewListIcon from '@mui/icons-material/ViewList'
import SearchIcon from '@mui/icons-material/Search'
import WidgetsIcon from '@mui/icons-material/Widgets'

/**
 * Embed block for articles. Stored in the HTML as an empty div with data-* attributes:
 *   <div data-embed="listing" data-religion="muslim" data-orderby="rating" data-limit="4"></div>
 *   <div data-embed="search"></div>
 * The website replaces the div with the real component. Every data-* attribute is kept,
 * also ones this editor does not know about.
 */

export type EmbedData = Record<string, string>

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    embed: {
      /** Insert an embed block, e.g. { 'data-embed': 'listing', 'data-limit': '4' } */
      insertEmbed: (data: EmbedData) => ReturnType
    }
  }
}

export const RELIGION_OPTIONS = [
  { value: '', label: 'Alle (ingen filter)' },
  { value: 'civil', label: 'Borgerlig' },
  { value: 'catholic', label: 'Katolsk' },
  { value: 'muslim', label: 'Muslimsk' },
  { value: 'jewish', label: 'Jødisk' },
  { value: 'buddhist', label: 'Buddhistisk' },
  { value: 'other', label: 'Andet' },
]

export const FUNERAL_TYPE_OPTIONS = [
  { value: '', label: 'Alle (ingen filter)' },
  { value: 'burial', label: 'Begravelse' },
  { value: 'cremation', label: 'Bisættelse' },
  { value: 'direct_cremation', label: 'Direkte kremering' },
  { value: 'ash_scattering', label: 'Askespredning' },
]

export const ORDERBY_OPTIONS = [
  { value: 'rating', label: 'Bedst vurderet' },
  { value: 'price', label: 'Pris' },
  { value: 'distance', label: 'Afstand' },
  { value: 'name', label: 'Navn' },
]

// Attributes edited in the dialog, in the order they are written to the HTML
const LISTING_KEYS = ['data-embed', 'data-religion', 'data-funeral-type', 'data-orderby', 'data-limit', 'data-title']

function collectDataAttributes(element: HTMLElement): EmbedData {
  const data: EmbedData = {}
  Array.from(element.attributes).forEach((attr) => {
    if (attr.name.startsWith('data-')) data[attr.name] = attr.value
  })
  return data
}

export function embedLabel(data: EmbedData): string {
  const type = data['data-embed']
  if (type === 'search') return 'Adressesøgning'
  if (type === 'listing') {
    const orderby = ORDERBY_OPTIONS.find((o) => o.value === data['data-orderby'])
    const parts = [
      data['data-religion'],
      data['data-funeral-type'],
      data['data-limit'],
      orderby?.label || data['data-orderby'],
    ].filter(Boolean)
    return parts.length ? `Bedemandsliste: ${parts.join(' · ')}` : 'Bedemandsliste'
  }
  return `Indlejring: ${type || 'ukendt'}`
}

interface EmbedDialogProps {
  data: EmbedData
  onClose: () => void
  onSave: (data: EmbedData) => void
  onDelete: () => void
}

function EmbedDialog({ data, onClose, onSave, onDelete }: EmbedDialogProps) {
  const type = data['data-embed']
  const [religion, setReligion] = useState(data['data-religion'] || '')
  const [funeralType, setFuneralType] = useState(data['data-funeral-type'] || '')
  const [orderby, setOrderby] = useState(data['data-orderby'] || 'rating')
  const [limit, setLimit] = useState(data['data-limit'] || '4')
  const [title, setTitle] = useState(data['data-title'] || '')

  const limitNumber = parseInt(limit, 10)
  const limitValid = !limit || (/^\d+$/.test(limit) && limitNumber >= 1 && limitNumber <= 50)

  const handleSave = () => {
    const next: EmbedData = { 'data-embed': 'listing' }
    // Empty filters are left out of the HTML, the site then shows all
    if (religion) next['data-religion'] = religion
    if (funeralType) next['data-funeral-type'] = funeralType
    next['data-orderby'] = orderby
    if (limit) next['data-limit'] = String(limitNumber)
    if (title.trim()) next['data-title'] = title.trim()
    // Keep data-* attributes this dialog does not edit
    Object.entries(data).forEach(([key, value]) => {
      if (!LISTING_KEYS.includes(key)) next[key] = value
    })
    onSave(next)
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{type === 'listing' ? 'Bedemandsliste' : embedLabel(data)}</DialogTitle>
      <DialogContent>
        {type === 'listing' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Religion</InputLabel>
              <Select value={religion} label="Religion" onChange={(e) => setReligion(e.target.value)}>
                {RELIGION_OPTIONS.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Begravelsesform</InputLabel>
              <Select value={funeralType} label="Begravelsesform" onChange={(e) => setFuneralType(e.target.value)}>
                {FUNERAL_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Sortering</InputLabel>
              <Select value={orderby} label="Sortering" onChange={(e) => setOrderby(e.target.value)}>
                {ORDERBY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label="Antal bedemænd"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              inputProps={{ min: 1, max: 50 }}
              error={!limitValid}
              helperText={limitValid ? 'Tom = sidens standard' : 'Skal være et tal mellem 1 og 50'}
            />
            <TextField
              size="small"
              label="Overskrift over listen"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              helperText="Valgfri"
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {type === 'search'
              ? 'Adressesøgningen har ingen indstillinger.'
              : 'Denne indlejring kan ikke redigeres her, men den bevares uændret.'}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button color="error" onClick={onDelete}>
          Fjern
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Annuller</Button>
          {type === 'listing' && (
            <Button variant="contained" onClick={handleSave} disabled={!limitValid}>
              Gem
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  )
}

function EmbedNodeView({ node, updateAttributes, deleteNode, selected, editor }: NodeViewProps) {
  const [open, setOpen] = useState(false)
  const data: EmbedData = node.attrs.data || {}
  const type = data['data-embed']
  const icon = type === 'search' ? <SearchIcon /> : type === 'listing' ? <ViewListIcon /> : <WidgetsIcon />

  return (
    <NodeViewWrapper data-drag-handle contentEditable={false} style={{ margin: '8px 0' }}>
      <Chip
        icon={icon}
        label={embedLabel(data)}
        color={selected ? 'primary' : 'default'}
        variant={selected ? 'filled' : 'outlined'}
        onClick={() => editor.isEditable && setOpen(true)}
        sx={{ fontWeight: 500, cursor: 'pointer' }}
      />
      {open && (
        <EmbedDialog
          data={data}
          onClose={() => setOpen(false)}
          onSave={(next) => {
            updateAttributes({ data: next })
            setOpen(false)
          }}
          onDelete={() => {
            setOpen(false)
            deleteNode()
          }}
        />
      )}
    </NodeViewWrapper>
  )
}

export const Embed = Node.create({
  name: 'embed',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      // All data-* attributes of the div, in their original order
      data: {
        default: {},
        parseHTML: (element: HTMLElement) => collectDataAttributes(element),
        renderHTML: (attributes: Record<string, any>) => attributes.data || {},
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-embed]', priority: 60 }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', HTMLAttributes]
  },

  addNodeView() {
    return ReactNodeViewRenderer(EmbedNodeView)
  },

  addCommands() {
    return {
      insertEmbed:
        (data) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { data } }),
    }
  },
})
